
import React, { useState, useEffect } from "react";
import { updateUserDetails } from "../apis/UserApis";
import { useAuth } from "./Authentication/AuthContext";
import { useToastHelpers } from "./Toasts/ToastUtils";
import { FaCog, FaSave, FaBriefcase, FaMapMarkerAlt, FaCode, FaChartLine } from "react-icons/fa";
import { useTheme } from "./Theme/ThemeContext";

export default function PreferencesSection({ form, preferences = {} }) {
  const { user, addUserDetails } = useAuth();
  const { currentTheme } = useTheme();
  const { successToast, errorToast } = useToastHelpers();

  const jobTypes = ["Job", "Internship", "Referral", "All"];
  const experienceLevels = [
    "All", "Freshers", "0-2 years", "0-3 years", "3-5 years",
    "2-5 years", "5-7 years", "7+ years",
  ];
  const locations = [
    "Pan India",
    "Bengaluru","Hyderabad","Pune","Chennai","Mumbai","Delhi NCR",
    "Noida","Gurugram","Kolkata","Ahmedabad","Jaipur","Chandigarh",
    "Coimbatore","Kochi","Indore","Nagpur","Surat","Mysuru",
    "Thiruvananthapuram","Bhubaneswar",
  ];
  const skillsList = [
    "JavaScript","React","Node.js","Java","Python","C++","Golang","SQL",
    "MongoDB","AWS","Docker","Kubernetes","HTML","CSS","TypeScript","Other",
  ];

  // Separate states
  const [jobType, setJobType] = useState([]);
  const [experienceLevel, setExperienceLevel] = useState([]);
  const [location, setLocation] = useState([]);
  const [skills, setSkills] = useState([]);
  const [otherSkills, setOtherSkills] = useState("");

  // Sync props -> state
  useEffect(() => {
    if (preferences) {
      setJobType(preferences.jobType || []);
      setExperienceLevel(preferences.experienceLevel || []);
      setLocation(preferences.location || []);
      setSkills(preferences.skills || []);
    }
  }, [preferences]);

  const toggleSelect = (value, field, setField, current) => {
    if (value === "Pan India" || value === "All") {
      setField(current.includes(value) ? [] : [value]);
      return;
    }
    const isExclusiveSelected =
      current.includes("Pan India") || current.includes("All");
    if (isExclusiveSelected) {
      setField([value]);
      return;
    }
    setField(
      current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value]
    );
  };

  const handleSave = async () => {
    try {
      const finalPrefs = {
        jobType,
        experienceLevel,
        location,
        skills: skills.includes("Other")
          ? [...skills.filter((s) => s !== "Other"), ...otherSkills.split(",").map((s) => s.trim())]
          : skills,
      };

      const updatedUser = { ...form, preferences: finalPrefs };
      await updateUserDetails(user.userId, updatedUser, user.token);
      addUserDetails(updatedUser);

      successToast("Preferences updated successfully!");
    } catch (err) {
      console.error(err);
      errorToast("Failed to update preferences.");
    }
  };

  const renderMultiSelect = (options, field, setField, current) => {
    const isExclusiveSelected =
      current.includes("Pan India") || current.includes("All");
    return (
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const isSelected = current.includes(opt);
          const isDisabled = isExclusiveSelected && !(current.includes(opt) || opt === "Pan India" || opt === "All");
          
          return (
            <button
              key={opt}
              type="button"
              onClick={() => !isDisabled && toggleSelect(opt, field, setField, current)}
              disabled={isDisabled}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                isSelected 
                  ? `bg-gradient-to-r ${currentTheme.colors.gradient} text-white shadow-md ${currentTheme.colors.glow}` 
                  : isDisabled 
                    ? "bg-neutral-100 dark:bg-neutral-700 text-neutral-300 dark:text-neutral-500 cursor-not-allowed" 
                    : "bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-200 dark:hover:bg-neutral-600"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    );
  };

  const SectionHeader = ({ icon: Icon, title }) => (
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-10 h-10 bg-gradient-to-br ${currentTheme.colors.gradientSubtle} rounded-xl flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-theme-primary" />
      </div>
      <label className="text-sm font-bold text-neutral-900 dark:text-white">{title}</label>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
      <div className="glass rounded-3xl shadow-xl border border-white/20 dark:border-white/10 p-8 sm:p-10">
        <div className="flex items-center gap-3 mb-8">
          <div className={`w-12 h-12 bg-gradient-to-br ${currentTheme.colors.gradient} rounded-2xl flex items-center justify-center shadow-lg ${currentTheme.colors.glow}`}>
            <FaCog className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-white">Preferences</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Customize your job recommendations</p>
          </div>
        </div>

        <div className="space-y-8">
          <div className="p-5 bg-neutral-50 dark:bg-neutral-700/50 rounded-2xl">
            <SectionHeader icon={FaBriefcase} title="Job Type" />
            {renderMultiSelect(jobTypes, "jobType", setJobType, jobType)}
          </div>
          
          <div className="p-5 bg-neutral-50 dark:bg-neutral-700/50 rounded-2xl">
            <SectionHeader icon={FaChartLine} title="Experience Level" />
            {renderMultiSelect(experienceLevels, "experienceLevel", setExperienceLevel, experienceLevel)}
          </div>
          
          <div className="p-5 bg-neutral-50 dark:bg-neutral-700/50 rounded-2xl">
            <SectionHeader icon={FaMapMarkerAlt} title="Preferred Locations" />
            {renderMultiSelect(locations, "location", setLocation, location)}
          </div>
          
          <div className="p-5 bg-neutral-50 dark:bg-neutral-700/50 rounded-2xl">
            <SectionHeader icon={FaCode} title="Skills" />
            {renderMultiSelect(skillsList, "skills", setSkills, skills)}
            {skills.includes("Other") && (
              <input
                type="text"
                value={otherSkills}
                onChange={(e) => setOtherSkills(e.target.value)}
                placeholder="Enter skills, comma separated"
                className="mt-4 w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus:bg-white dark:focus:bg-neutral-600 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 transition-all outline-none text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500"
              />
            )}
          </div>
        </div>

        <div className="mt-10 text-center">
          <button
            onClick={handleSave}
            className={`inline-flex items-center gap-2 bg-gradient-to-r ${currentTheme.colors.gradient} hover:opacity-90 text-white font-semibold py-3.5 px-8 rounded-xl shadow-lg ${currentTheme.colors.glow} hover:shadow-xl transition-all duration-300 hover:scale-[1.02]`}
          >
            <FaSave className="w-4 h-4" />
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
}
